/**
 * Analytics Dashboard Screen
 * 
 * Comprehensive analytics dashboard combining:
 * - User Activity Monitoring
 * - Performance Metrics
 * - Analytics & Reporting
 * 
 * @module AnalyticsDashboardScreen
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  ShoppingCart,
  Clock,
  Download,
  RefreshCw,
  Calendar,
  Store,
  FileText,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useActivityMonitoring, type ActivityStatsDateRange } from '../../hooks/useActivityMonitoring';
import { PerformanceMetricsService, TeamPerformanceMetrics } from '../../services/PerformanceMetricsService';
import { AnalyticsService, AnalyticsMetrics, AnalyticsPeriod } from '../../services/AnalyticsService';
import { ActivityLog } from '../../components/manager/ActivityLog';
import { AnalyticsChart } from '../../components/manager/AnalyticsChart';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { getLocalCalendarYMD } from '../../utils/calendarDate';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.xl};
  border-bottom: 2px solid ${ThemeColors.border.light};
`;

const Tab = styled.button<{ active: boolean }>`
  padding: ${Spacing.md} ${Spacing.lg};
  border: none;
  background: none;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.secondary};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s ease;

  &:hover {
    color: ${ThemeColors.primary.main};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${Spacing.lg};
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.md};
`;

const StatTitle = styled.h3`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  text-transform: uppercase;
`;

const StatIcon = styled.div<{ color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || ThemeColors.primary.light};
  color: ${props => props.color ? 'white' : ThemeColors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.sm} 0;
`;

const StatSubtitle = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.xs};
`;

const TrendIndicator = styled.div<{ isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${props => props.isPositive ? ThemeColors.success.main : ThemeColors.error.main};
`;

const PeriodSelector = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const PeriodButton = styled.button<{ active: boolean }>`
  padding: ${Spacing.xs} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.medium};
  background: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : ThemeColors.text.primary};
  border-radius: 6px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    ${props => !props.active && `
      background: ${ThemeColors.gray[100]};
    `}
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xl};
  color: ${ThemeColors.text.secondary};
  gap: ${Spacing.sm};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    animation: spin 1s linear infinite;
  }
`;

const MetricSection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
  gap: ${Spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover {
      background: ${ThemeColors.primary.dark};
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.medium};
    
    &:hover {
      background: ${ThemeColors.gray[100]};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Section = styled.div`
  margin-bottom: ${Spacing.xxl};
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.lg};
`;

const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const PerformanceCard = styled.div`
  background: white;
  padding: ${Spacing.lg};
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const PerformerName = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const PerformerMetrics = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

/**
 * Analytics Dashboard Screen Component
 */
const last30Days = (): ActivityStatsDateRange => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { start, end };
};

export const AnalyticsDashboardScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'performance'>('overview');
  const [teamMetrics, setTeamMetrics] = useState<TeamPerformanceMetrics | null>(null);
  const [analyticsMetrics, setAnalyticsMetrics] = useState<AnalyticsMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [period, setPeriod] = useState<AnalyticsPeriod>('month');
  const [error, setError] = useState<string | null>(null);
  // Custom date range state
  const [customStart, setCustomStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return getLocalCalendarYMD(d);
  });
  const [customEnd, setCustomEnd] = useState<string>(() => getLocalCalendarYMD(new Date()));
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const statsRange = React.useMemo(() => last30Days(), []);

  const {
    activities,
    statistics,
    isLoading: isLoadingActivities,
    refreshActivities,
    fetchStatistics
  } = useActivityMonitoring(undefined, 50, statsRange);

  const fetchMetrics = React.useCallback(async () => {
    try {
      setIsLoadingMetrics(true);
      setError(null);
      const startDate = period === 'custom' ? new Date(customStart + 'T00:00:00') : undefined;
      const endDate = period === 'custom' ? new Date(customEnd + 'T23:59:59') : undefined;
      const [analytics, team] = await Promise.all([
        AnalyticsService.getAnalyticsMetrics(period, startDate, endDate),
        PerformanceMetricsService.getTeamPerformance('monthly')
      ]);
      setAnalyticsMetrics(analytics);
      setTeamMetrics(team);
    } catch (err: any) {
      console.error('Error fetching metrics:', err);
      setError(err?.message || 'Failed to load analytics');
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Handle export
   */
  const handleExport = () => {
    alert('Export functionality coming soon!');
  };

  /**
   * Handle refresh: reload activities, activity stats (last 30 days), and metrics
   */
  const handleRefresh = async () => {
    await Promise.all([
      refreshActivities(),
      fetchStatistics(statsRange.start, statsRange.end),
      fetchMetrics()
    ]);
  };

  /**
   * Format period label
   */
  const getPeriodLabel = (p: AnalyticsPeriod): string => {
    switch (p) {
      case 'today': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      case 'custom': return period === 'custom' ? `${customStart} → ${customEnd}` : 'Custom';
      default: return 'Custom';
    }
  };

  const handleCustomApply = async () => {
    const start = new Date(customStart + 'T00:00:00').getTime();
    const end = new Date(customEnd + 'T23:59:59').getTime();
    if (start > end) {
      setError('Start date must be before end date');
      return;
    }
    setError(null);
    setPeriod('custom');
    setShowCustomPicker(false);
    try {
      setIsLoadingMetrics(true);
      const startDate = new Date(customStart + 'T00:00:00');
      const endDate = new Date(customEnd + 'T23:59:59');
      const [analytics, team] = await Promise.all([
        AnalyticsService.getAnalyticsMetrics('custom', startDate, endDate),
        PerformanceMetricsService.getTeamPerformance('monthly')
      ]);
      setAnalyticsMetrics(analytics);
      setTeamMetrics(team);
    } catch (err: any) {
      setError(err?.message || 'Failed to load analytics');
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  /**
   * Format trend percentage
   */
  const formatTrend = (value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>Analytics & Performance Dashboard</Title>
        </Header>

        {/* Tabs */}
        <TabContainer>
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <Activity size={18} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
            Overview
          </Tab>
          <Tab active={activeTab === 'activity'} onClick={() => setActiveTab('activity')}>
            <Clock size={18} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
            Activity Log
          </Tab>
          <Tab active={activeTab === 'performance'} onClick={() => setActiveTab('performance')}>
            <TrendingUp size={18} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
            Performance
          </Tab>
        </TabContainer>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Period Selector */}
            <PeriodSelector>
              {(['today', 'week', 'month', 'year'] as AnalyticsPeriod[]).map((p) => (
                <PeriodButton
                  key={p}
                  active={period === p && !showCustomPicker}
                  onClick={() => { setShowCustomPicker(false); setPeriod(p); }}
                >
                  {getPeriodLabel(p)}
                </PeriodButton>
              ))}
              <PeriodButton
                active={period === 'custom' || showCustomPicker}
                onClick={() => setShowCustomPicker(!showCustomPicker)}
              >
                <Calendar size={14} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                Custom
              </PeriodButton>
              {showCustomPicker && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: Spacing.sm,
                  flexWrap: 'wrap',
                  padding: Spacing.md,
                  background: ThemeColors.background.secondary,
                  borderRadius: 8,
                  border: `1px solid ${ThemeColors.border.light}`,
                  marginTop: Spacing.xs
                }}>
                  <label style={{ fontSize: Typography.fontSize.sm, fontWeight: Typography.fontWeight.medium }}>
                    From:
                  </label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    style={{
                      padding: `${Spacing.xs} ${Spacing.sm}`,
                      border: `1px solid ${ThemeColors.border.medium}`,
                      borderRadius: 6,
                      fontSize: Typography.fontSize.sm
                    }}
                  />
                  <label style={{ fontSize: Typography.fontSize.sm, fontWeight: Typography.fontWeight.medium }}>
                    To:
                  </label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    style={{
                      padding: `${Spacing.xs} ${Spacing.sm}`,
                      border: `1px solid ${ThemeColors.border.medium}`,
                      borderRadius: 6,
                      fontSize: Typography.fontSize.sm
                    }}
                  />
                  <Button variant="primary" onClick={handleCustomApply}>
                    Apply
                  </Button>
                  <Button variant="secondary" onClick={() => setShowCustomPicker(false)}>
                    Cancel
                  </Button>
                </div>
              )}
            </PeriodSelector>

            {isLoadingMetrics ? (
              <LoadingSpinner>
                <RefreshCw size={24} />
                Loading metrics...
              </LoadingSpinner>
            ) : error ? (
              <div style={{ 
                padding: Spacing.xl, 
                textAlign: 'center', 
                color: ThemeColors.error.main,
                background: ThemeColors.error.light + '20',
                borderRadius: '8px',
                marginBottom: Spacing.lg
              }}>
                <div style={{ fontWeight: Typography.fontWeight.semiBold, marginBottom: Spacing.sm }}>
                  Error loading analytics
                </div>
                <div style={{ fontSize: Typography.fontSize.sm }}>{error}</div>
                <Button 
                  variant="primary" 
                  onClick={handleRefresh}
                  disabled={isLoadingMetrics}
                  style={{ marginTop: Spacing.md }}
                >
                  <RefreshCw />
                  Try Again
                </Button>
              </div>
            ) : (
              <>
                {/* Primary Metrics */}
                <MetricSection>
                  <SectionHeader>
                    <SectionTitle>Business Overview</SectionTitle>
                    <Button variant="secondary" onClick={handleRefresh} disabled={isLoadingMetrics}>
                      <RefreshCw />
                      Refresh
                    </Button>
                  </SectionHeader>
                  <StatsGrid>
                    {/* Total Stores */}
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Total Stores</StatTitle>
                        <StatIcon color={ThemeColors.primary.main}>
                          <Store />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{analyticsMetrics?.totalStores || 0}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.storesVisited || 0} visited this period
                        {analyticsMetrics?.periodComparison?.storesGrowth !== undefined && (
                          <TrendIndicator isPositive={analyticsMetrics.periodComparison.storesGrowth >= 0}>
                            {analyticsMetrics.periodComparison.storesGrowth >= 0 ? (
                              <ArrowUpRight size={14} />
                            ) : (
                              <ArrowDownRight size={14} />
                            )}
                            {formatTrend(analyticsMetrics.periodComparison.storesGrowth)}
                          </TrendIndicator>
                        )}
                      </StatSubtitle>
                    </StatCard>

                    {/* Total Notes */}
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Total Notes</StatTitle>
                        <StatIcon color={ThemeColors.info.main}>
                          <FileText />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{analyticsMetrics?.totalNotes || 0}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.notesThisPeriod || 0} this period
                        {analyticsMetrics?.averageNotesPerStore > 0 && (
                          <> • Avg: {analyticsMetrics.averageNotesPerStore.toFixed(1)} per store</>
                        )}
                        {analyticsMetrics?.periodComparison?.notesGrowth !== undefined && (
                          <TrendIndicator isPositive={analyticsMetrics.periodComparison.notesGrowth >= 0}>
                            {analyticsMetrics.periodComparison.notesGrowth >= 0 ? (
                              <ArrowUpRight size={14} />
                            ) : (
                              <ArrowDownRight size={14} />
                            )}
                            {formatTrend(analyticsMetrics.periodComparison.notesGrowth)}
                          </TrendIndicator>
                        )}
                      </StatSubtitle>
                    </StatCard>

                    {/* Total Orders */}
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Total Orders</StatTitle>
                        <StatIcon color={ThemeColors.success.main}>
                          <ShoppingCart />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{analyticsMetrics?.ordersThisPeriod || 0}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.completedOrders || 0} completed • {analyticsMetrics?.pendingOrders || 0} pending
                        {analyticsMetrics?.periodComparison?.ordersGrowth !== undefined && (
                          <TrendIndicator isPositive={analyticsMetrics.periodComparison.ordersGrowth >= 0}>
                            {analyticsMetrics.periodComparison.ordersGrowth >= 0 ? (
                              <ArrowUpRight size={14} />
                            ) : (
                              <ArrowDownRight size={14} />
                            )}
                            {formatTrend(analyticsMetrics.periodComparison.ordersGrowth)}
                          </TrendIndicator>
                        )}
                      </StatSubtitle>
                    </StatCard>

                    {/* Total Revenue */}
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Total Revenue</StatTitle>
                        <StatIcon color={ThemeColors.warning.main}>
                          <DollarSign />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>${(analyticsMetrics?.revenueThisPeriod || 0).toLocaleString()}</StatValue>
                      <StatSubtitle>
                        Avg order: ${(analyticsMetrics?.averageOrderValue || 0).toLocaleString()}
                        {analyticsMetrics?.periodComparison?.revenueGrowth !== undefined && (
                          <TrendIndicator isPositive={analyticsMetrics.periodComparison.revenueGrowth >= 0}>
                            {analyticsMetrics.periodComparison.revenueGrowth >= 0 ? (
                              <ArrowUpRight size={14} />
                            ) : (
                              <ArrowDownRight size={14} />
                            )}
                            {formatTrend(analyticsMetrics.periodComparison.revenueGrowth)}
                          </TrendIndicator>
                        )}
                      </StatSubtitle>
                    </StatCard>
                  </StatsGrid>
                </MetricSection>

                {/* Secondary Metrics */}
                <MetricSection>
                  <SectionTitle>Additional Metrics</SectionTitle>
                  <StatsRow>
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Stores with Notes</StatTitle>
                        <StatIcon color={ThemeColors.info.light}>
                          <FileText />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{analyticsMetrics?.storesWithNotes || 0}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.totalStores > 0 
                          ? `${((analyticsMetrics.storesWithNotes / analyticsMetrics.totalStores) * 100).toFixed(1)}% of total stores`
                          : 'No stores'}
                      </StatSubtitle>
                    </StatCard>

                    <StatCard>
                      <StatHeader>
                        <StatTitle>Stores with Orders</StatTitle>
                        <StatIcon color={ThemeColors.success.light}>
                          <ShoppingCart />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{analyticsMetrics?.storesWithOrders || 0}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.totalStores > 0 
                          ? `${((analyticsMetrics.storesWithOrders / analyticsMetrics.totalStores) * 100).toFixed(1)}% of total stores`
                          : 'No stores'}
                      </StatSubtitle>
                    </StatCard>

                    <StatCard>
                      <StatHeader>
                        <StatTitle>Avg Orders/Store</StatTitle>
                        <StatIcon color={ThemeColors.primary.light}>
                          <TrendingUp />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{(analyticsMetrics?.averageOrdersPerStore ?? 0).toFixed(2)}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.ordersThisPeriod || 0} orders across {analyticsMetrics?.totalStores || 0} stores
                      </StatSubtitle>
                    </StatCard>

                    <StatCard>
                      <StatHeader>
                        <StatTitle>Avg Revenue/Store</StatTitle>
                        <StatIcon color={ThemeColors.warning.light}>
                          <DollarSign />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>${(analyticsMetrics?.averageRevenuePerStore || 0).toLocaleString()}</StatValue>
                      <StatSubtitle>
                        {analyticsMetrics?.revenueThisPeriod || 0} revenue across {analyticsMetrics?.totalStores || 0} stores
                      </StatSubtitle>
                    </StatCard>
                  </StatsRow>
                </MetricSection>

                {/* Activity Metrics */}
                <MetricSection>
                  <SectionTitle>Activity Summary</SectionTitle>
                  <StatsRow>
                    <StatCard>
                      <StatHeader>
                        <StatTitle>Total Activities</StatTitle>
                        <StatIcon>
                          <Activity />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{statistics?.totalActivities || 0}</StatValue>
                      <StatSubtitle>Last 30 days</StatSubtitle>
                    </StatCard>

                    <StatCard>
                      <StatHeader>
                        <StatTitle>Active Users</StatTitle>
                        <StatIcon color={ThemeColors.success.main}>
                          <Users />
                        </StatIcon>
                      </StatHeader>
                      <StatValue>{teamMetrics?.activeUsers || 0}</StatValue>
                      <StatSubtitle>of {teamMetrics?.totalUsers || 0} total users</StatSubtitle>
                    </StatCard>
                  </StatsRow>
                </MetricSection>
              </>
            )}

            <AnalyticsChart defaultPreset="30d" />

            <Section>
              <SectionTitle>Top Performers</SectionTitle>
              <PerformanceGrid>
                {teamMetrics?.topPerformers.slice(0, 3).map((performer, index) => (
                  <PerformanceCard key={performer.userId}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.md, marginBottom: Spacing.md }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: Typography.fontWeight.bold
                      }}>
                        #{index + 1}
                      </div>
                      <div>
                        <PerformerName>{performer.userName}</PerformerName>
                        <div style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                          {performer.userEmail}
                        </div>
                      </div>
                    </div>
                    <PerformerMetrics>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          {performer.totalOrders}
                        </div>
                        <div>Orders</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          ${performer.totalRevenue.toLocaleString()}
                        </div>
                        <div>Revenue</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          {performer.orderCompletionRate.toFixed(0)}%
                        </div>
                        <div>Completion</div>
                      </div>
                    </PerformerMetrics>
                  </PerformanceCard>
                ))}
              </PerformanceGrid>
            </Section>

            <Section>
              <SectionTitle>Recent Activity</SectionTitle>
              <ActivityLog 
                activities={statistics?.recentActivities || []} 
                isLoading={isLoadingActivities}
              />
            </Section>
          </>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <>
            <Toolbar>
              <div style={{ fontSize: Typography.fontSize.lg, fontWeight: Typography.fontWeight.semiBold }}>
                Activity History
              </div>
              <div style={{ display: 'flex', gap: Spacing.sm }}>
                <Button variant="secondary" onClick={handleRefresh}>
                  <RefreshCw />
                  Refresh
                </Button>
                <Button variant="secondary" onClick={handleExport}>
                  <Download />
                  Export
                </Button>
              </div>
            </Toolbar>

            <ActivityLog 
              activities={activities} 
              isLoading={isLoadingActivities}
            />
          </>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <>
            <Toolbar>
              <div style={{ fontSize: Typography.fontSize.lg, fontWeight: Typography.fontWeight.semiBold }}>
                Team Performance
              </div>
              <div style={{ display: 'flex', gap: Spacing.sm }}>
                <Button variant="secondary">
                  <Calendar />
                  This Month
                </Button>
                <Button variant="secondary" onClick={handleExport}>
                  <Download />
                  Export Report
                </Button>
              </div>
            </Toolbar>

            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatTitle>Avg Orders/User</StatTitle>
                  <StatIcon color={ThemeColors.primary.main}>
                    <ShoppingCart />
                  </StatIcon>
                </StatHeader>
                <StatValue>{(teamMetrics?.averageOrdersPerUser || 0).toFixed(1)}</StatValue>
                <StatSubtitle>Per active user</StatSubtitle>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatTitle>Avg Revenue/User</StatTitle>
                  <StatIcon color={ThemeColors.success.main}>
                    <DollarSign />
                  </StatIcon>
                </StatHeader>
                <StatValue>${(teamMetrics?.averageRevenuePerUser || 0).toLocaleString()}</StatValue>
                <StatSubtitle>Per active user</StatSubtitle>
              </StatCard>
            </StatsGrid>

            <Section>
              <SectionTitle>All Team Members</SectionTitle>
              <PerformanceGrid>
                {teamMetrics?.topPerformers.map((performer, index) => (
                  <PerformanceCard key={performer.userId}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.md, marginBottom: Spacing.md }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: Typography.fontWeight.bold
                      }}>
                        #{index + 1}
                      </div>
                      <div>
                        <PerformerName>{performer.userName}</PerformerName>
                        <div style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                          {performer.userEmail}
                        </div>
                      </div>
                    </div>
                    <PerformerMetrics>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          {performer.totalOrders}
                        </div>
                        <div>Orders</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          ${performer.totalRevenue.toLocaleString()}
                        </div>
                        <div>Revenue</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: Typography.fontWeight.semiBold, color: ThemeColors.text.primary }}>
                          ${performer.averageOrderValue.toFixed(0)}
                        </div>
                        <div>Avg Order</div>
                      </div>
                    </PerformerMetrics>
                  </PerformanceCard>
                ))}
              </PerformanceGrid>
            </Section>
          </>
        )}
      </MainContent>
    </PageContainer>
  );
};

